// View controls component for zoom and toggle options

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import './toolbar-well.js';
import './toolbar-button.js';

/**
 * View controls component
 * Provides zoom controls and toggle buttons for grid/walls
 */
class ViewControls extends BaseComponent {
  static properties = {
    zoom: { type: Number },
    showGrid: { type: Boolean, attribute: 'show-grid' },
    showGridToggle: { type: Boolean, attribute: 'show-grid-toggle' },
    showWalls: { type: Boolean, attribute: 'show-walls' },
    showWallsToggle: { type: Boolean, attribute: 'show-walls-toggle' },
    showCenterButton: { type: Boolean, attribute: 'show-center-button' },
    showPlaybackControls: { type: Boolean, attribute: 'show-playback-controls' },
    paused: { type: Boolean },
    spectatorBadge: { type: Boolean, attribute: 'spectator-badge' },
  };

  static styles = css`
    @import url('https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css');
    :host {
      display: block;
      position: absolute;
      bottom: 20px;
      right: 20px;
      z-index: 100;
    }

    .zoom-percentage {
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: #fff;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 14px;
      font-family: monospace;
      font-weight: bold;
      min-width: 50px;
      cursor: default;
    }

    .control-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #333;
      border: 1px solid #555;
      color: #fff;
      padding: 8px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      font-weight: bold;
      transition: all 0.2s;
      min-width: 50px;
    }

    .control-button:hover {
      background: #444;
      border-color: #666;
    }

    .control-button:active {
      background: #222;
      transform: translateY(1px);
    }

    .control-button.active {
      background: #0066cc;
      border-color: #0066cc;
    }

    .icon-container {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
    }

    .icon-container .base-icon {
      position: absolute;
      top: 0;
      left: 0;
      width: 24px;
      height: 24px;
    }

    .icon-container .overlay-icon {
      position: absolute;
      bottom: -2px;
      right: -2px;
      background: #333;
      border-radius: 50%;
      padding: 1px;
    }

    .icon-container .center-overlay {
      bottom: auto;
      right: auto;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(51, 51, 51, 0.8);
      border-radius: 50%;
      padding: 1px;
    }

    .control-button.active .icon-container .overlay-icon {
      background: #0066cc;
    }

    .spacer {
      width: 1px;
      height: 24px;
      background: rgba(255, 255, 255, 0.2);
    }

    .badge-wrapper {
      position: relative;
    }

    .badge-wrapper:hover {
      z-index: 1002;
    }

    .badge {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 6px;
      color: #fff;
      font-size: 22px;
      line-height: 1;
      cursor: default;
    }

    .badge-tooltip {
      position: absolute;
      left: 50%;
      bottom: 100%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.9);
      color: #fff;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      white-space: nowrap;
      margin-bottom: 8px;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s;
      z-index: 1001;
    }

    .badge-wrapper:hover .badge-tooltip {
      opacity: 1;
    }

    /* Icon sizing lives on toolbar-button via ::slotted(svg). */

    .icon-sm {
      width: 14px;
      height: 14px;
    }

    .icon-md {
      width: 16px;
      height: 16px;
    }
  `;

  constructor() {
    super();
    this.zoom = 1;
    this.showGrid = true;
    this.showGridToggle = true;
    this.showWalls = false;
    this.showWallsToggle = true;
    this.showCenterButton = true;
    this.showPlaybackControls = false;
    this.paused = false;
    this.spectatorBadge = false;
  }

  get _zoomPercentage() {
    return Math.round(this.zoom * 100);
  }

  _handleZoomIn() {
    this.dispatchEvent(new CustomEvent('zoom-in', { bubbles: true }));
  }

  _handleZoomOut() {
    this.dispatchEvent(new CustomEvent('zoom-out', { bubbles: true }));
  }

  _handleResetZoom() {
    this.dispatchEvent(new CustomEvent('reset-zoom', { bubbles: true }));
  }

  _handleToggleGrid() {
    this.dispatchEvent(new CustomEvent('toggle-grid', { bubbles: true }));
  }

  _handleToggleWalls() {
    this.dispatchEvent(new CustomEvent('toggle-walls', { bubbles: true }));
  }

  _handleCenterOnTokens() {
    this.dispatchEvent(new CustomEvent('center-on-tokens', { bubbles: true }));
  }

  _handleRestart() {
    this.dispatchEvent(new CustomEvent('playback-restart', { bubbles: true }));
  }

  _handleStep() {
    this.dispatchEvent(new CustomEvent('playback-step', { bubbles: true }));
  }

  _handleTogglePlay() {
    this.dispatchEvent(new CustomEvent('playback-toggle', { bubbles: true }));
  }

  _renderWallsIcon() {
    return html`
      <div class="icon-container">
        <!-- BrickWall icon -->
        <svg
          class="icon base-icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect width="18" height="18" x="3" y="3" rx="2" />
          <path d="M12 9v6" />
          <path d="M16 15v6" />
          <path d="M16 3v6" />
          <path d="M3 15h18" />
          <path d="M3 9h18" />
          <path d="M8 15v6" />
          <path d="M8 3v6" />
        </svg>
        <!-- Eye icon -->
        <svg
          class="icon-sm overlay-icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </div>
    `;
  }

  _renderGridIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <rect width="18" height="18" x="3" y="3" rx="2" />
        <path d="M3 9h18" />
        <path d="M3 15h18" />
        <path d="M9 3v18" />
        <path d="M15 3v18" />
      </svg>
    `;
  }

  _renderCenterIcon() {
    return html`
      <div class="icon-container">
        <!-- Scan icon -->
        <svg
          class="icon base-icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M3 7V5a2 2 0 0 1 2-2h2" />
          <path d="M17 3h2a2 2 0 0 1 2 2v2" />
          <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
          <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
        </svg>
        <!-- User icon -->
        <svg
          class="icon-md overlay-icon center-overlay"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      </div>
    `;
  }

  _renderMinusIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M5 12h14" />
      </svg>
    `;
  }

  _renderBinocularsIcon() {
    return html`<i class="bi bi-binoculars-fill"></i>`;
  }

  _renderRestartIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <path d="M3 3v5h5" />
      </svg>
    `;
  }

  _renderStepIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polygon points="5 4 15 12 5 20 5 4" />
        <line x1="19" y1="5" x2="19" y2="19" />
      </svg>
    `;
  }

  _renderPlayIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polygon points="6 4 20 12 6 20 6 4" />
      </svg>
    `;
  }

  _renderPauseIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <rect x="6" y="4" width="4" height="16" />
        <rect x="14" y="4" width="4" height="16" />
      </svg>
    `;
  }

  _renderPlusIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M5 12h14" />
        <path d="M12 5v14" />
      </svg>
    `;
  }

  render() {
    return html`
      <toolbar-well direction="row">
        ${
          this.spectatorBadge
            ? html`
              <div class="badge-wrapper">
                <div class="badge">${this._renderBinocularsIcon()}</div>
                <div class="badge-tooltip">Watching Replay</div>
              </div>
            `
            : ''
        }
        ${
          this.showPlaybackControls
            ? html`
              <toolbar-button tooltip="Restart from the beginning" tooltip-position="top" @click="${this._handleRestart}">
                ${this._renderRestartIcon()}
              </toolbar-button>
              <toolbar-button
                tooltip="${this.paused ? 'Play' : 'Pause'}"
                tooltip-position="top"
                @click="${this._handleTogglePlay}"
              >
                ${this.paused ? this._renderPlayIcon() : this._renderPauseIcon()}
              </toolbar-button>
              <toolbar-button tooltip="Step one event" tooltip-position="top" @click="${this._handleStep}">
                ${this._renderStepIcon()}
              </toolbar-button>
              <div class="spacer"></div>
            `
            : ''
        }
        ${
          this.showWallsToggle
            ? html`
              <toolbar-button
                ?active="${this.showWalls}"
                tooltip="Toggle Walls/Openings"
                tooltip-position="top"
                @click="${this._handleToggleWalls}"
              >
                ${this._renderWallsIcon()}
              </toolbar-button>
            `
            : ''
        }
        ${
          this.showGridToggle
            ? html`
              <toolbar-button
                ?active="${this.showGrid}"
                tooltip="Toggle Grid"
                tooltip-position="top"
                @click="${this._handleToggleGrid}"
              >
                ${this._renderGridIcon()}
              </toolbar-button>
              <div class="spacer"></div>
            `
            : ''
        }
        ${
          this.showCenterButton
            ? html`
              <toolbar-button tooltip="Center on My Tokens" tooltip-position="top" @click="${this._handleCenterOnTokens}">
                ${this._renderCenterIcon()}
              </toolbar-button>
              <div class="spacer"></div>
            `
            : ''
        }

        <toolbar-button tooltip="Zoom Out" tooltip-position="top" @click="${this._handleZoomOut}">
          ${this._renderMinusIcon()}
        </toolbar-button>

        <toolbar-button tooltip="Reset Zoom (100%)" tooltip-position="top" @click="${this._handleResetZoom}">
          100%
        </toolbar-button>

        <toolbar-button tooltip="Zoom In" tooltip-position="top" @click="${this._handleZoomIn}">
          ${this._renderPlusIcon()}
        </toolbar-button>

        <button class="zoom-percentage" disabled>${this._zoomPercentage}%</button>
      </toolbar-well>
    `;
  }
}

customElements.define('view-controls', ViewControls);
